import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const emphasisWordPulseSkill: Skill = {
    name: SKILL_NAMES.emphasisWordPulse,
    kind: 'codeo',
    description:
        'Pulse one key word with color and a slight scale the instant it is spoken — the accent ' +
        'hit for the verb or noun that carries the moment. One word per scene, maximum.',
    instructions: `# Emphasis Word Pulse

One word in an on-screen line — the key verb, the stat qualifier, the brand noun — snaps to the accent color with a small scale pulse at the exact instant the narrator says it. It's the visual equivalent of vocal stress, and it only works if it's rare: one word per scene, not every scene.

## Markup

Wrap the emphasis word in its own span, \`inline-block\` so scale applies.

\`\`\`html
<p id="verdict" style="position: absolute; left: 160px; top: 460px; margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 72px; color: var(--l5-color-0, #101820);">
  Switching is completely <span id="kw-free" style="display: inline-block;">free</span>
</p>
\`\`\`

## Timeline code

\`ONSET\` is the emphasized word's spoken second, taken straight from the scene's \`cues:\` line in the video state, where each word is prefixed \`[N.Ns]\`. Snap in fast, settle slower, keep the color:

\`\`\`js
const tl = gsap.timeline({ paused: true });
const ONSET = 2.4; // seconds, from the [N.Ns] cue for the emphasized word
tl.from('#verdict', { opacity: 0, y: 20, duration: 0.5, ease: 'power3.out' }, 0.0);
tl.to('#kw-free', { color: 'var(--l5-color-1, #7a6dff)', scale: 1.12, duration: 0.16, ease: 'power2.out' }, ONSET);
tl.to('#kw-free', { scale: 1, duration: 0.34, ease: 'power2.inOut' }, ONSET + 0.16);
window.__timelines = { main: tl };
\`\`\`

The color stays at the accent after the pulse — it reads as a highlight the narrator left behind. Reverting the color erases the emphasis.

## Adapt

- Stronger hit for the scene's single biggest word: \`scale: 1.18\` with \`ease: 'back.out(1.7)'\` on the settle instead.
- Weight-pulse variant when the palette is monochrome: tweening \`fontVariationSettings\` is unreliable — instead render the word in a heavier weight and pulse \`scale\` only.
- Pair with a draw-on underline (see ${SKILL_NAMES.svgDrawOn}) landing at the same onset for the scene's one climax beat — once.

## Pitfalls

- Fire exactly at \`ONSET\` — emphasis is the one reveal that should NOT lead the audio. Early color shifts read as random recoloring.
- The span scales — \`display: inline-block\` required, no \`transform\` in its CSS, and \`transformOrigin\` defaults to center, which is correct here.
- The pulse assumes the line is already on screen: the line's entrance must start before \`ONSET\` (the line enters, then the word hits).
- The color/scale tweens are \`to\` tweens, so they animate from the live rendered state — fine even though the span has no CSS transform. Never use \`from\`/\`set\` with an explicit transform channel on an element that also carries a CSS \`transform\`.
- Check the accent color's contrast against the backdrop — an accent that fails WCAG AA (4.5:1 body, 3:1 large) on this background needs a different emphasis color.`,
};
